import { Ativo, Device, Leitura } from "../api/m5";

export interface MapaDispositivos {
  assetNome: (deviceId: string) => string;
}

export function criarMapaDispositivos(devices: Device[], ativos: Ativo[]): MapaDispositivos {
  const nomePorAsset = new Map(ativos.map((a) => [a.id, a.nome]));
  const assetPorDevice = new Map(devices.map((d) => [d.device_id, d.asset_id]));
  return {
    assetNome: (deviceId) => {
      const assetId = assetPorDevice.get(deviceId);
      return (assetId && nomePorAsset.get(assetId)) || deviceId;
    },
  };
}

/**
 * Agrupa leituras em series pro grafico: chave base = tag_indice. Quando ha
 * mais de uma estacao entre as leituras da mesma tag, prefixa pelo nome da
 * estacao - misturar leituras de estacoes diferentes numa unica serie fazia
 * um zigue-zague sem sentido (interpolava valores de locais fisicos
 * distintos como se fossem uma sequencia temporal continua). Estacao e' o
 * eixo primario de separacao; se dentro de UMA estacao ha mais de um device
 * reportando a mesma tag, so ai prefixa pelo device_id.
 */
export function agruparPorSerie(
  leituras: Leitura[],
  mapa: MapaDispositivos
): Record<string, { timestamp: string; valor: number }[]> {
  const estacoesPorTag = new Map<string, Set<string>>();
  const devicesPorTagEstacao = new Map<string, Set<string>>();
  for (const l of leituras) {
    const tagChave = `${l.tag}_${l.indice}`;
    const estacao = mapa.assetNome(l.device_id);
    if (!estacoesPorTag.has(tagChave)) estacoesPorTag.set(tagChave, new Set());
    estacoesPorTag.get(tagChave)!.add(estacao);

    const chaveTagEstacao = `${tagChave}|${estacao}`;
    if (!devicesPorTagEstacao.has(chaveTagEstacao)) devicesPorTagEstacao.set(chaveTagEstacao, new Set());
    devicesPorTagEstacao.get(chaveTagEstacao)!.add(l.device_id);
  }

  const grupos: Record<string, { timestamp: string; valor: number }[]> = {};
  for (const l of [...leituras].sort((a, b) => a.timestamp_medicao.localeCompare(b.timestamp_medicao))) {
    const tagChave = `${l.tag}_${l.indice}`;
    const estacao = mapa.assetNome(l.device_id);
    const multiEstacao = estacoesPorTag.get(tagChave)!.size > 1;
    const multiDeviceNaEstacao = devicesPorTagEstacao.get(`${tagChave}|${estacao}`)!.size > 1;

    let chave = tagChave;
    if (multiEstacao) chave = `${estacao} · ${tagChave}`;
    else if (multiDeviceNaEstacao) chave = `${l.device_id} · ${tagChave}`;

    if (!grupos[chave]) grupos[chave] = [];
    grupos[chave].push({ timestamp: l.timestamp_medicao, valor: l.valor });
  }
  return grupos;
}

/**
 * Extrai o "tag_indice" de uma chave de serie (que pode vir prefixada por
 * estacao/device - ver agruparPorSerie). Usado pra aplicar uma escolha de
 * eixo (principal/secundario) a uma variavel inteira de uma vez, nao serie
 * por serie: ex. marcar TA como secundario vale pra TA de todas as estacoes.
 */
export function extrairTagChave(chaveSerie: string): string {
  const partes = chaveSerie.split(" · ");
  return partes[partes.length - 1];
}

export interface LinhaPivotada {
  estacao: string;
  timestamp: string;
  valores: Record<string, number>;
}

/**
 * Pivota leituras em uma linha por (estacao, timestamp), colunas dinamicas
 * por variavel (tag, ou tag_indice quando indice > 1) - mesma estrutura
 * usada pela Tabela e pela exportacao CSV (Tabela e Grafico), pra exportar
 * exatamente o que aparece na tela. So' e' seguro fundir por estacao (nao
 * por device) porque processed_reading garante no maximo uma leitura por
 * (estacao, tag, indice, timestamp) desde a migration 005 do M2.
 */
export function agruparPorTimestamp(leituras: Leitura[], mapa: MapaDispositivos): LinhaPivotada[] {
  const porChave = new Map<string, LinhaPivotada>();
  for (const l of leituras) {
    const estacao = mapa.assetNome(l.device_id);
    const chave = `${estacao}|${l.timestamp_medicao}`;
    let linha = porChave.get(chave);
    if (!linha) {
      linha = { estacao, timestamp: l.timestamp_medicao, valores: {} };
      porChave.set(chave, linha);
    }
    const coluna = l.indice === 1 ? l.tag : `${l.tag}_${l.indice}`;
    linha.valores[coluna] = l.valor;
  }
  return [...porChave.values()].sort((a, b) => b.timestamp.localeCompare(a.timestamp));
}
